import type { DailySchedulePlan, ScheduleJobInput } from './types';
import { planToPublishPayload } from './planDailySchedule';
import { parseComplianceDispatchError } from '../utils/complianceReadiness';

export interface MissingOrgJob {
  id: string;
  title: string;
}

/** Job IDs that would be inserted as HQ trips on publish (non-fixed legs). */
export function publishableJobIds(plan: DailySchedulePlan): string[] {
  const ids = new Set<string>();
  for (const a of planToPublishPayload(plan).assignments) {
    for (const leg of a.legs) ids.add(leg.job_id);
  }
  return [...ids];
}

/**
 * HQ publish copies job.org_id onto trips (source=hq). DB requires org_id unless source=owner.
 */
export function jobsMissingOrgForHqPublish(plan: DailySchedulePlan): MissingOrgJob[] {
  const jobsById = new Map((plan.jobs ?? []).map((j) => [j.id, j] as const));
  const titles = plan.jobTitles ?? {};
  const missing: MissingOrgJob[] = [];

  for (const jobId of publishableJobIds(plan)) {
    const job = jobsById.get(jobId);
    if (jobHasOrg(job)) continue;
    missing.push({
      id: jobId,
      title: titles[jobId] ?? job?.title ?? `Job ${jobId.slice(0, 8)}`,
    });
  }

  return missing;
}

function jobHasOrg(job: ScheduleJobInput | undefined): boolean {
  if (!job) return false;
  const org = job.orgId;
  return typeof org === 'string' && org.trim().length > 0;
}

const ORG_CONSTRAINT = 'trips_org_required_unless_owner';
const ORG_CODE = 'JOB_ORG_REQUIRED';

export type PublishScheduleErrorKind = 'empty' | 'job_org_required' | 'db_check' | 'raw';

export interface PublishScheduleError {
  kind: PublishScheduleErrorKind;
  names: string;
}

export function classifyPublishScheduleError(
  raw: string,
  missingHint?: MissingOrgJob[]
): PublishScheduleError {
  const text = raw.trim();
  if (!text) return { kind: 'empty', names: '' };

  if (text.includes(ORG_CODE) || text.includes(ORG_CONSTRAINT)) {
    const names =
      missingHint && missingHint.length > 0
        ? missingHint.map((j) => j.title).join(', ')
        : extractJobOrgRequiredIds(text)
            .map((id) => `Job ${id.slice(0, 8)}`)
            .join(', ');
    return { kind: 'job_org_required', names };
  }

  if (/violates check constraint|Failing row contains|new row for relation/i.test(text)) {
    return { kind: 'db_check', names: '' };
  }

  return { kind: 'raw', names: '' };
}

/**
 * Map PostgREST / Postgres publish failures to operator-facing English.
 * Never surface raw constraint names or "Failing row" dumps in the UI.
 */
export function formatPublishScheduleError(
  raw: string,
  missingHint?: MissingOrgJob[]
): string {
  const compliance = parseComplianceDispatchError(raw);
  if (compliance.kind === 'DRIVER_LICENSE_EXPIRED') {
    return `Cannot publish: driver licence expired${compliance.expiry ? ` (${compliance.expiry})` : ''} on schedule date${compliance.onDate ? ` ${compliance.onDate}` : ''}. Choose another driver.`;
  }
  if (compliance.kind === 'VEHICLE_REGISTRATION_EXPIRED') {
    return `Cannot publish: vehicle registration expired${compliance.expiry ? ` (${compliance.expiry})` : ''} on schedule date${compliance.onDate ? ` ${compliance.onDate}` : ''}. Choose another vehicle.`;
  }

  if (/Driver schedule conflict/i.test(raw)) {
    return (
      `${raw.trim()}. ` +
      `The same driver is on two jobs whose times overlap within the schedule buffer (default 15 minutes) — often because Preferred Driver was applied to more than one vehicle. ` +
      `Change the driver on one vehicle (or clear it) and try again.`
    );
  }

  if (/Vehicle schedule conflict/i.test(raw)) {
    return (
      `${raw.trim()}. ` +
      `The vehicle already has an overlapping trip within the schedule buffer (default 15 minutes). Reassign the job or leave a larger gap.`
    );
  }

  const classified = classifyPublishScheduleError(raw, missingHint);

  switch (classified.kind) {
    case 'empty':
      return 'Publish failed. Try again or contact support.';
    case 'job_org_required': {
      const list = classified.names ? ` Missing customer: ${classified.names}.` : '';
      return `Cannot publish: each HQ job needs a Customer (organisation).${list} Add a Customer on the schedule (or in Jobs); assignments are kept.`;
    }
    case 'db_check':
      return 'Publish rejected by the database. Check job Customer, vehicle capacity, and conflicts, then try again.';
    case 'raw':
      return raw.trim();
  }
}

function extractJobOrgRequiredIds(message: string): string[] {
  const ids: string[] = [];
  const re = /JOB_ORG_REQUIRED:([0-9a-f-]{36})/gi;
  let m: RegExpExecArray | null;
  while ((m = re.exec(message)) !== null) {
    ids.push(m[1]);
  }
  return ids;
}
